import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useSEO } from "@/hooks/useSEO";
import { Search, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ["All", "Article", "Judgment", "Checklist"];
const tags = ["All", "Contracts", "Compliance", "Disputes", "International", "Middle East", "UK", "India", "Startups", "Employment"];

export default function InsightsPage() {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeTag, setActiveTag] = useState("All");
  useSEO("Insights | Equaliser Legal LLP", "Articles, checklists, and practical guidance on contracts, compliance, disputes, and cross-border matters from Equaliser Legal LLP.");

  useEffect(() => {
    const params = {};
    if (activeCategory !== "All") params.category = activeCategory;
    if (activeTag !== "All") params.tag = activeTag;

    axios.get(`${API}/insights`, { params }).then(res => {
      setInsights(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [activeCategory, activeTag]);

  return (
    <div data-testid="insights-page">
      {/* Hero */}
      <section className="bg-[#0A1628] noise-overlay relative pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">Insights</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-medium leading-tight tracking-tight max-w-3xl">
            Articles, notes, and practical guidance.
          </h1>
          <p className="text-white/50 text-base md:text-lg leading-relaxed max-w-2xl mt-6">
            Our publications cover contracts, compliance, disputes, and cross-border matters relevant to businesses in India and abroad.
          </p>
        </div>
      </section>

      {/* Filters & Content */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Category Filters */}
          <div className="flex flex-wrap gap-3 mb-8">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-category-${cat.toLowerCase()}`}
                onClick={() => setActiveCategory(cat)}
                className={`text-xs uppercase tracking-wider px-4 py-2 rounded-[2px] transition-colors duration-300 ${
                  activeCategory === cat
                    ? "bg-[#0A1628] text-white"
                    : "border border-slate-200 text-slate-500 hover:border-[#0A1628] hover:text-[#0A1628]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Tag Filters */}
          <div className="flex flex-wrap gap-2 mb-12">
            {tags.map((tag) => (
              <button
                key={tag}
                data-testid={`filter-tag-${tag.toLowerCase()}`}
                onClick={() => setActiveTag(tag)}
                className={`text-xs px-3 py-1.5 rounded-[2px] transition-colors duration-300 ${
                  activeTag === tag
                    ? "bg-[#C5A059] text-white"
                    : "text-slate-400 hover:text-[#C5A059]"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="text-center py-16">
              <p className="text-slate-400">Loading insights...</p>
            </div>
          ) : insights.length === 0 ? (
            <div className="text-center py-16">
              <Search size={32} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-400">No insights found for the selected filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {insights.map((insight) => (
                <Link
                  to={`/insights/${insight.id}`}
                  key={insight.id}
                  data-testid={`insight-card-${insight.id}`}
                  className="border border-slate-200 p-8 hover:border-[#0A1628] transition-colors duration-300 group flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded-[2px] ${
                      insight.category === "Article" ? "bg-[#0A1628]/5 text-[#0A1628]" :
                      insight.category === "Judgment" ? "bg-[#C5A059]/10 text-[#C5A059]" :
                      "bg-slate-100 text-slate-500"
                    }`}>
                      {insight.category}
                    </span>
                    <span className="text-xs text-slate-400">{insight.published_date}</span>
                  </div>
                  <h3 className="font-heading text-lg text-[#0A1628] font-medium mb-3 group-hover:text-[#C5A059] transition-colors duration-300 leading-snug flex-grow">
                    {insight.title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed line-clamp-3 mb-4">{insight.excerpt}</p>
                  <div className="flex flex-wrap gap-1.5 mt-auto">
                    {insight.tags.map((tag, j) => (
                      <span key={j} className="text-[10px] text-slate-400 border border-slate-200 px-2 py-0.5 rounded-[2px]">
                        {tag}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
